import { useMemo } from 'react';
import { DemoChip, RoiProductionValueStatsMetric, StatsMetric, StatsMetricProps } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { formatters, useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { featureFlags } from '../../../feature-flags';
import { summaryTabsStyles } from '../styles';

interface RoiSummaryProps {
  isDemoAccount?: boolean;
  isLoading?: boolean;
  productionAppointmentsCount: number;
  productionPatientsCount?: number;
  summaryCardsData: StatsMetricProps[];
}

export const RoiSummary = ({
  isDemoAccount,
  isLoading,
  productionAppointmentsCount,
  productionPatientsCount,
  summaryCardsData,
}: RoiSummaryProps) => {
  const { t } = useTranslation('analytics');
  const { isSmallMode } = useIsSmallMode();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const showProductionValue = !getFeatureFlagValue(featureFlags.hideRoiProductionValue);

  const data = useMemo(() => {
    if (showProductionValue) {
      return summaryCardsData;
    }

    // Include appointments count card in the summary cards data
    return [
      ...summaryCardsData,
      {
        uniqueId: 'appointments-completed',
        title: t('Appointments Completed'),
        data: [
          {
            label: t('{{productionPatientsCount}} Patients', { productionPatientsCount }),
            value: formatters.value(productionAppointmentsCount),
            infoHoverText: t(
              'A total of {{productionAppointmentsCount}} appointments were completed by {{productionPatientsCount}} patients',
              { productionAppointmentsCount, productionPatientsCount }
            ),
          },
        ],
      },
    ];
  }, [productionAppointmentsCount, productionPatientsCount, showProductionValue, summaryCardsData, t]);

  return (
    <div css={summaryTabsStyles.mainWrapper} style={{ padding: isSmallMode ? theme.spacing(2) : theme.spacing(3) }}>
      {isDemoAccount && <DemoChip style={{ marginBottom: theme.spacing(1) }} />}
      <div css={summaryTabsStyles.statsWrapper}>
        {data.map((props) => {
          return <StatsMetric {...props} isLoading={isLoading} key={props.uniqueId} />;
        })}
        {showProductionValue && (
          <RoiProductionValueStatsMetric isLoading={isLoading} appointmentsCount={productionAppointmentsCount} />
        )}
      </div>
    </div>
  );
};
